import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, pipe } from 'rxjs';
import { catchError, concatMap, finalize, first, map, switchMap, tap } from 'rxjs/operators';

import { AddBoxErrorDialogComponent } from '../../pages/main/cybersecurity-cockpit/customer-dashboard/add-box-error-dialog/add-box-error-dialog.component';
import { AddBoxConfirmationDialogComponent } from '../../pages/main/cybersecurity-cockpit/customer-dashboard/add-box-confirmation-dialog/add-box-confirmation-dialog.component';

import { DashboardState, initialDashboardState } from '../models/state/dashboard.state';
import { ApplicationService, BoxService, CustomerService, DialogService, PentestService } from '../services';
import {
  Box,
  BoxCreateRequest,
  BoxStatus,
  Customer,
  GetScanResultSummaryData,
  PentestScan,
  PentestScanPageRequest,
  PentestScanResultSummary,
  UpdateDataSharingData,
} from '../models';
import { RegistrationErrorType } from '../enums';
import { Router } from '@angular/router';
import { AddBoxInfoDialogComponent } from '../../pages/main/cybersecurity-cockpit/customer-dashboard/add-box-info-dialog/add-box-info-dialog.component';

export const DashboardStore = signalStore(
  withState<DashboardState>(initialDashboardState),
  withMethods(
    (
      store,
      applicationService = inject(ApplicationService),
      pentestService = inject(PentestService),
      boxService = inject(BoxService),
      customerService = inject(CustomerService),
      router = inject(Router),
      dialogService = inject(DialogService),
      toastr = inject(ToastrService),
      translate = inject(TranslateService),
    ) => {
      const loadPentestScans = rxMethod<{ pageRequest: PentestScanPageRequest; customer: Customer | undefined }>(
        pipe(
          map(({ pageRequest, customer }) => {
            const pentestScanPageRequest = { ...pageRequest, page: 0, size: applicationService.itemsPerPage() };
            patchState(store, { scansLoading: true, pentestScans: [], pentestScanPageRequest });
            return { pentestScanPageRequest, customer };
          }),
          switchMap(({ pentestScanPageRequest, customer }) =>
            pentestService.getPentestScans(customer?.id, pentestScanPageRequest).pipe(
              tap((pentestScans) => patchState(store, { pentestScans: [...pentestScans], scansLoading: false })),
              switchMap((pentestScans) =>
                getPentestScanResults(pentestService, pentestScans, customer, store.pentestScanResults()).pipe(
                  tap(({ pentestScans, pentestScanResults }) =>
                    patchState(store, {
                      pentestScans: [...pentestScans],
                      pentestScanResults: { ...pentestScanResults },
                    }),
                  ),
                  catchError(() => of()), // Prevent rxMethod from crashing
                ),
              ),
              catchError(() => {
                patchState(store, { scansLoading: false });
                return of();
              }),
            ),
          ),
        ),
      );

      const loadMorePentestScans = rxMethod<Customer | undefined>(
        pipe(
          map((customer) => {
            const { page, ...restOfPageRequest } = store.pentestScanPageRequest();
            const pentestScanPageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
            patchState(store, { pentestScanPageRequest, scansMoreLoading: true });
            return { customer, pentestScanPageRequest };
          }),
          concatMap(({ customer, pentestScanPageRequest }) =>
            pentestService.getPentestScans(customer?.id, pentestScanPageRequest).pipe(
              map((_pentestScans) => {
                const pentestScans = [...store.pentestScans(), ..._pentestScans];
                patchState(store, { pentestScans });
                return pentestScans;
              }),
              concatMap((pentestScans) =>
                getPentestScanResults(pentestService, pentestScans, customer, store.pentestScanResults()).pipe(
                  tap(({ pentestScans, pentestScanResults }) =>
                    patchState(store, {
                      pentestScans: [...pentestScans],
                      pentestScanResults: { ...pentestScanResults },
                      scansMoreLoading: false,
                    }),
                  ),
                ),
              ),
              catchError(() => {
                patchState(store, { scansMoreLoading: false });
                return of();
              }), // Prevent rxMethod from crashing
            ),
          ),
        ),
      );

      const loadPentestStatistics = rxMethod<Customer | undefined>(
        pipe(
          tap(() =>
            patchState(store, {
              statisticsLoading: true,
              pentestStatistics: { ...initialDashboardState.pentestStatistics },
            }),
          ),
          switchMap((customer) =>
            pentestService.getPentestStatistics(customer).pipe(
              tap((pentestStatistics) => patchState(store, { pentestStatistics })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { statisticsLoading: false })),
            ),
          ),
        ),
      );

      const loadPentestScanStatistics = rxMethod<Customer | undefined>(
        pipe(
          tap(() =>
            patchState(store, {
              statisticsLoading: true,
              customerPentestScanStatistics: [],
            }),
          ),
          switchMap((customer) =>
            pentestService.getCustomerPentestScanStatistics(customer).pipe(
              tap((customerPentestScanStatistics) => patchState(store, { customerPentestScanStatistics })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { statisticsLoading: false })),
            ),
          ),
        ),
      );

      const loadBoxes = rxMethod<Customer | undefined>(
        pipe(
          map((customer) => {
            const boxPageRequest = {
              ...store.boxPageRequest(),
              page: 0,
              size: applicationService.itemsPerPage(),
            };
            patchState(store, { boxesLoading: true, boxes: [], boxPageRequest });
            return { customer, boxPageRequest };
          }),
          switchMap(({ customer, boxPageRequest }) =>
            boxService.getCustomerBoxes(customer?.id, boxPageRequest).pipe(
              tap((boxes) => patchState(store, { boxes, boxesLoading: false })),
              switchMap((boxes) =>
                getBoxStatuses(boxService, boxes, store.boxStatuses(), customer?.id).pipe(
                  tap(({ boxes, boxStatuses }) =>
                    patchState(store, {
                      boxes: [...boxes],
                      boxStatuses: { ...boxStatuses },
                    }),
                  ),
                ),
              ),
              catchError(() => {
                patchState(store, { boxesLoading: false });
                return of();
              }),
            ),
          ),
        ),
      );

      const loadMoreBoxes = rxMethod<number | undefined>(
        pipe(
          map((customerId) => {
            const { page, ...restOfPageRequest } = store.boxPageRequest();
            const boxPageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
            patchState(store, { boxPageRequest });
            return { customerId, boxPageRequest };
          }),
          concatMap(({ customerId, boxPageRequest }) =>
            boxService.getUserBoxes(boxPageRequest).pipe(
              map((_boxes) => {
                const boxes = [...store.boxes(), ..._boxes];
                patchState(store, { boxes });
                return boxes;
              }),
              concatMap((boxes) =>
                getBoxStatuses(boxService, boxes, store.boxStatuses(), customerId).pipe(
                  tap(({ boxes, boxStatuses }) =>
                    patchState(store, {
                      boxes: [...boxes],
                      boxStatuses: { ...boxStatuses },
                    }),
                  ),
                ),
              ),
              catchError(() => of()), // Prevent rxMethod from crashing
            ),
          ),
        ),
      );

      const loadLightBoxes = rxMethod<Customer | undefined>(
        switchMap((customer) =>
          boxService.getAllBoxes(customer?.id).pipe(
            tap((allBoxes) => patchState(store, { allBoxes })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );

      const loadAssets = rxMethod<Customer | undefined>(
        switchMap((customer) =>
          pentestService.getAssets(customer?.id).pipe(
            tap((assets) => patchState(store, { assets })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );

      const loadDataSharingStatus = rxMethod<void>(
        pipe(
          switchMap(() =>
            customerService.getDataSharingStatus().pipe(
              tap((initialized) => patchState(store, { isDataSharingInitialized: initialized })),
              catchError(() => of()), // Prevent rxMethod from crashing
            ),
          ),
        ),
      );

      const updateDataSharingData = rxMethod<UpdateDataSharingData>(
        pipe(switchMap((data) => customerService.updateSharingData(data))),
      );

      const addBox = rxMethod<{ request: BoxCreateRequest; customerId?: number }>(
        pipe(
          switchMap(({ request, customerId }) =>
            boxService.createBox(customerId, request).pipe(
              tap(({ created, box }) => {
                if (created) {
                  patchState(store, { boxes: [...store.boxes(), box] });
                  patchState(store, { allBoxes: [...store.allBoxes(), box] });
                  toastr.success(translate.instant('boxes.alerts.createSuccess'));
                  return;
                }
                dialogService.openDialog(AddBoxInfoDialogComponent, { box });
              }),
              catchError((error) => {
                const errorType = RegistrationErrorType.fromErrorCode(error.status);
                return dialogService
                  .openDialog(AddBoxErrorDialogComponent, { errorType })
                  .beforeClosed()
                  .pipe(
                    first(),
                    tap((doAction) => {
                      if (doAction && errorType === RegistrationErrorType.BoxOffline) {
                        addBox({ request, customerId: customerId });
                        return;
                      }
                      router.navigate([], { queryParams: {}, replaceUrl: true }).then();
                    }),
                  );
              }),
            ),
          ),
        ),
      );

      const confirmAddBox = rxMethod<{ qrCodeId: string; customerId: number | undefined }>(
        pipe(
          switchMap(({ qrCodeId, customerId }) =>
            boxService.getBoxByQrCode(customerId, qrCodeId).pipe(
              switchMap((box) =>
                dialogService
                  .openDialog(AddBoxConfirmationDialogComponent, { box })
                  .beforeClosed()
                  .pipe(
                    first(),
                    tap((doAction) => {
                      if (!doAction) {
                        router.navigate([], { queryParams: {}, replaceUrl: true }).then();
                        return;
                      }
                      addBox({ request: { qrCodeId }, customerId: customerId });
                    }),
                  ),
              ),
              catchError(() => of()), // Prevent rxMethod from crashing
            ),
          ),
        ),
      );

      return {
        loadPentestScans,
        loadMorePentestScans,
        loadPentestScanStatistics,
        loadPentestStatistics,
        loadBoxes,
        loadMoreBoxes,
        loadLightBoxes,
        loadAssets,
        loadDataSharingStatus,
        updateDataSharingData,
        confirmAddBox,
      };
    },
  ),
);

const getPentestScanResults = (
  pentestService: PentestService,
  _pentestScans: PentestScan[],
  customer: Customer | null | undefined,
  pentestScanResults: { [key: string]: PentestScanResultSummary },
): Observable<{ pentestScans: PentestScan[]; pentestScanResults: { [key: string]: PentestScanResultSummary } }> => {
  const request: GetScanResultSummaryData[] = _pentestScans
    .filter((scan) => !pentestScanResults[scan.historyId ?? scan.id])
    .map((scan) => ({ id: scan.id, historyId: scan.historyId, scanResultType: scan.scanResultType }));

  return (request.length ? pentestService.getPentestScanResultsSummary(request, customer) : of([])).pipe(
    map((scanResults) => {
      scanResults.forEach((scanResult) => (pentestScanResults[scanResult.id] = scanResult));
      const pentestScans = _pentestScans.map((scan) => {
        const pentestScanResult = pentestScanResults[scan.historyId ?? scan.id];
        return pentestScanResult
          ? {
              ...scan,
              resultStatus: pentestScanResult.status,
            }
          : scan;
      });
      return { pentestScans, pentestScanResults };
    }),
  );
};

const getBoxStatuses = (
  boxService: BoxService,
  _boxes: Box[],
  _boxStatuses: { [key: string]: BoxStatus },
  customerId?: number,
): Observable<{
  boxes: Box[];
  boxStatuses: { [key: string]: BoxStatus };
}> => {
  const request: string[] = _boxes.filter((box) => !_boxStatuses[box.id]).map((box) => box.id);

  return (request.length ? boxService.getBoxStatuses(request, customerId) : of([])).pipe(
    map((boxStatuses) => {
      boxStatuses.forEach((boxStatus) => (_boxStatuses[boxStatus.id] = boxStatus));
      const boxes = _boxes.map((box) => {
        const boxStatus = _boxStatuses[box.id];
        return boxStatus ? { ...box, status: boxStatus.status } : box;
      });
      return { boxes, boxStatuses: _boxStatuses };
    }),
  );
};
